import { IResourceComponentsProps, useApiUrl } from '@pankod/refine-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CustomPage } from '../../components/customPage';
import {
  GoogleMap,
  useLoadScript,
  Marker,
  MarkerClusterer,
  InfoWindow,
} from '@react-google-maps/api';
import UserHandler from 'util/UserHandler';
import { OwnershipType } from 'types/ownership/ownershipType';
import axios from 'axios';
import { UserRoleEnum } from 'types/users/userRole';
import { Button, Typography, Progress } from '@pankod/refine-antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  HomeOutlined,
  CloseOutlined,
  TeamOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  BarChartOutlined,
  MessageOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  ShopOutlined,
  CreditCardOutlined,
  DollarOutlined,
  FileDoneOutlined,
  UserOutlined,
  FileTextOutlined,
  CheckCircleOutlined, // Ajout de l'importation
} from '@ant-design/icons';

const { Title, Text } = Typography;

const containerStyle = {
  width: '100%',
  height: 'calc(100vh - 32vh)',
  borderRadius: '20px',
  overflow: 'hidden',
  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
};

interface ContractStat {
  month: string; // Format: "YYYY-MM"
  type: string; // Type de contrat (ex: "rent", "sale")
  status: string; // Statut du contrat (ex: "granted", "pending", "rejected")
  count: number; // Nombre de contrats
}

interface Stats {
  totalUsers: number;
  uniqueVisitors: number;
  totalContrats: number;
  pendingVillas: number;
  totalOwnerships: number;
  validatedListings: number;
  refusedListings: number;
  totalMessages: number;
  activeUsersToday: number;
  activeUsersWeek: number;
  activeUsersMonth: number;
  newUsersMonth: number;
  userRoles: { admin: number; agency: number; private: number; user: number; host: number };
  listingTypes: { rent: number; sale: number };
  listingStatuses: { pending: number; validated: number; refused: number };
  offersThisMonth: number;
  avgValidationTime: number;
  contractCount: number;
  contractTypes: { type: string; total: number }[];
  contractStatuses: { status: string; total: number }[];
  contractStats: ContractStat[];
}

const Home: React.FC<IResourceComponentsProps> = () => {
  const apiUrl = useApiUrl();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GEOCODE_API_KEY || '',
  });

  const [dataCache, setDataCache] = useState<{
    stats: Stats | null;
    points: { id: string; type: OwnershipType; position: { lat: number; lng: number }; info?: { name: string; address: string; city: string } }[] | null;
    lastFetch: number;
  }>({
    stats: null,
    points: null,
    lastFetch: 0,
  });

  const authUser = useMemo(() => {
    const user = UserHandler.getInstance().user;
    return user ? { id: user.id, role: user.role, token: UserHandler.getInstance().token } : null;
  }, []);

  const [currentPos, setCurrentPos] = useState<{ lat: number; lng: number }>({ lat: 12.313838, lng: -1.467639 });
  const [points, setPoints] = useState<{
    id: string;
    type: OwnershipType;
    position: { lat: number; lng: number };
    info?: { name: string; address: string; city: string };
  }[]>([]);
  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'generale' | 'utilisateurs' | 'annonces' | 'contrats' | 'map'>('generale');
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    uniqueVisitors: 0,
    totalContrats: 0,
    pendingVillas: 0,
    totalOwnerships: 0,
    validatedListings: 0,
    refusedListings: 0,
    totalMessages: 0,
    activeUsersToday: 0,
    activeUsersWeek: 0,
    activeUsersMonth: 0,
    newUsersMonth: 0,
    userRoles: { admin: 0, agency: 0, private: 0, user: 0, host: 0 },
    listingTypes: { rent: 0, sale: 0 },
    listingStatuses: { pending: 0, validated: 0, refused: 0 },
    offersThisMonth: 0,
    avgValidationTime: 0,
    contractCount: 0,
    contractTypes: [],
    contractStatuses: [],
    contractStats: [],
  });

  useEffect(() => {
    let isMounted = true;
    const watchId = navigator.geolocation.watchPosition(
      (lastPosition) => {
        const newPos = {
          lat: lastPosition.coords.latitude,
          lng: lastPosition.coords.longitude,
        };
        if (
          isMounted &&
          (Math.abs(newPos.lat - currentPos.lat) > 0.0001 || Math.abs(newPos.lng - currentPos.lng) > 0.0001)
        ) {
          setCurrentPos(newPos);
        }
      },
      (error) => console.error('Erreur de géolocalisation:', error),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 1000 }
    );

    return () => {
      isMounted = false;
      navigator.geolocation.clearWatch(watchId);
    };
  }, [currentPos]);

  const fetchOwnerships = useCallback(async () => {
    if (!authUser) return;
    try {
      const endpoint =
        authUser.role === UserRoleEnum.Admin
          ? `${apiUrl}/ownership`
          : `${apiUrl}/ownership/user/${authUser.id}`;

      const response = await axios.get(endpoint, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser.token}`,
        },
      });
      const mappedPoints = response.data?.data?.map((o: any) => ({
        id: o.id,
        type: o.type,
        position: { lat: parseFloat(o.latitude), lng: parseFloat(o.longitude) },
        info: { name: o.name, address: o.address, city: o.city },
      })) || [];

      setPoints(mappedPoints);
      setDataCache((prev) => ({ ...prev, points: mappedPoints, lastFetch: Date.now() }));
      setStats((prev) => ({ ...prev, totalContrats: mappedPoints.length }));
    } catch (err) {
      console.error('Échec de la récupération des propriétés:', err);
    }
  }, [apiUrl, authUser]);

  const fetchStats = useCallback(async () => {
    if (!authUser) return;
    const defaultStats: Stats = {
      totalUsers: 0,
      uniqueVisitors: 0,
      totalContrats: 0,
      pendingVillas: 0,
      totalOwnerships: 0,
      validatedListings: 0,
      refusedListings: 0,
      totalMessages: 0,
      activeUsersToday: 0,
      activeUsersWeek: 0,
      activeUsersMonth: 0,
      newUsersMonth: 0,
      userRoles: { admin: 0, agency: 0, private: 0, user: 0, host: 0 },
      listingTypes: { rent: 0, sale: 0 },
      listingStatuses: { pending: 0, validated: 0, refused: 0 },
      offersThisMonth: 0,
      avgValidationTime: 0,
      contractCount: 0,
      contractTypes: [],
      contractStatuses: [],
      contractStats: [],
    };

    try {
      const [
        userCountResponse,
        userRolesResponse,
        ownershipCountResponse,
        ownershipByTypeResponse,
        ownershipByStatusResponse,
        conveyanceCountResponse,
        conveyanceByTypeResponse,
        conveyanceByStatusResponse,
        usersResponse,
        contractStatsResponse,
      ] = await Promise.all([
        axios.get(`${apiUrl}/user/count`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
            'Cache-Control': 'no-cache',
          },
        }).catch((err) => {
          console.error('Erreur sur /user/count:', err.response?.data || err.message);
          return { data: null };
        }),
        axios.get(`${apiUrl}/user/count-by-role`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
            'Cache-Control': 'no-cache',
          },
        }).catch((err) => {
          console.error('Erreur sur /user/count-by-role:', err.response?.data || err.message);
          return { data: null };
        }),
        axios.get(`${apiUrl}/ownership/count`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
            'Cache-Control': 'no-cache',
          },
        }).catch((err) => {
          console.error('Erreur sur /ownership/count:', err.response?.data || err.message);
          return { data: null };
        }),
        axios.get(`${apiUrl}/ownership/count/by-type`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
            'Cache-Control': 'no-cache',
          },
        }).catch((err) => {
          console.error('Erreur sur /ownership/count/by-type:', err.response?.data || err.message);
          return { data: null };
        }),
        axios.get(`${apiUrl}/ownership/count/by-status`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
            'Cache-Control': 'no-cache',
          },
        }).catch((err) => {
          console.error('Erreur sur /ownership/count/by-status:', err.response?.data || err.message);
          return { data: null };
        }),
        axios.get(`${apiUrl}/conveyance/count`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
            'Cache-Control': 'no-cache',
          },
        }).catch((err) => {
          console.error('Erreur sur /conveyance/count:', err.response?.data || err.message);
          return { data: null };
        }),
        axios.get(`${apiUrl}/conveyance/count/by-type`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
            'Cache-Control': 'no-cache',
          },
        }).catch((err) => {
          console.error('Erreur sur /conveyance/count/by-type:', err.response?.data || err.message);
          return { data: null };
        }),
        axios.get(`${apiUrl}/conveyance/count/by-status`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
            'Cache-Control': 'no-cache',
          },
        }).catch((err) => {
          console.error('Erreur sur /conveyance/count/by-status:', err.response?.data || err.message);
          return { data: null };
        }),
        axios.get(`${apiUrl}/user/stats`, {
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authUser.token}` },
        }).catch((err) => {
          console.error('Erreur sur /user/stats:', err.response?.data || err.message);
          return { data: null };
        }),
        axios.get(`${apiUrl}/conveyance/count/by-month-type-status`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser.token}`,
            'Cache-Control': 'no-cache',
          },
        }).catch((err) => {
          console.error('Erreur sur /conveyance/count/by-month-type-status:', err.response?.data || err.message);
          return { data: null };
        }),
      ]);

      const newStats: Stats = { ...defaultStats };

      if (userCountResponse.data) {
        newStats.totalUsers = userCountResponse.data.totalUsers || 0;
      }

      if (userRolesResponse.data) {
        const rolesData = userRolesResponse.data.data || [];
        newStats.userRoles = {
          admin: rolesData.find((r: { role: string }) => r.role === 'admin')?.total || 0,
          agency: rolesData.find((r: { role: string }) => r.role === 'agency')?.total || 0,
          private: rolesData.find((r: { role: string }) => r.role === 'private')?.total || 0,
          user: rolesData.find((r: { role: string }) => r.role === 'user')?.total || 0,
          host: rolesData.find((r: { role: string }) => r.role === 'host')?.total || 0,
        };
      }

      if (ownershipCountResponse.data) {
        newStats.totalOwnerships = ownershipCountResponse.data.total || 0;
      }

      if (ownershipByTypeResponse.data) {
        const typesData = ownershipByTypeResponse.data.data || [];
        newStats.listingTypes = {
          rent: typesData.find((t: { type: string }) => t.type === 'rent')?.total || 0,
          sale: typesData.find((t: { type: string }) => t.type === 'sale')?.total || 0,
        };
      }

      if (ownershipByStatusResponse.data) {
        const statusData = ownershipByStatusResponse.data.data || [];
        newStats.listingStatuses = {
          pending: statusData.find((s: { status: string }) => s.status === 'pending')?.total || 0,
          validated: statusData.find((s: { status: string }) => s.status === 'accepted')?.total || 0,
          refused: statusData.find((s: { status: string }) => s.status === 'rejected')?.total || 0,
        };
        newStats.pendingVillas = newStats.listingStatuses.pending;
        newStats.validatedListings = newStats.listingStatuses.validated;
        newStats.refusedListings = newStats.listingStatuses.refused;
      }

      if (conveyanceCountResponse.data) {
        newStats.contractCount = conveyanceCountResponse.data.total || 0;
      }

      if (conveyanceByTypeResponse.data) {
        newStats.contractTypes = conveyanceByTypeResponse.data.data || [];
      }

      if (conveyanceByStatusResponse.data) {
        newStats.contractStatuses = conveyanceByStatusResponse.data.data || [];
      }

      if (usersResponse.data) {
        newStats.newUsersMonth = usersResponse.data.newThisMonth || 0;
        newStats.activeUsersToday = usersResponse.data.activeToday || 0;
        newStats.activeUsersWeek = usersResponse.data.activeWeek || 0;
        newStats.activeUsersMonth = usersResponse.data.activeMonth || 0;
      }

      if (contractStatsResponse.data) {
        newStats.contractStats = contractStatsResponse.data.data || [];
      }

      setStats(newStats);
      setDataCache((prev) => ({ ...prev, stats: newStats, lastFetch: Date.now() }));
    } catch (err) {
      console.error('Échec global de la récupération des statistiques:', err);
      setStats(defaultStats);
    }
  }, [apiUrl, authUser]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (!authUser || !isMounted) return;

      const now = Date.now();
      if (dataCache.lastFetch && now - dataCache.lastFetch < 60000) {
        if (dataCache.stats) setStats(dataCache.stats);
        if (dataCache.points) setPoints(dataCache.points);
        return;
      }
      await Promise.all([fetchOwnerships(), fetchStats()]);
    };

    fetchData();
    const intervalId = setInterval(fetchData, 60000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [fetchOwnerships, fetchStats, authUser, dataCache]);

  const handleActiveMarker = useCallback((marker: string | null) => {
    if (marker === activeMarker) return;
    setActiveMarker(marker);
  }, [activeMarker]);

  const options = {
    imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
    gridSize: 60,
    minimumClusterSize: 2,
  };

  const mapOptions = {
    scrollwheel: true,
  };

  const renderMap = () => (
    <div className="map-container">
      <Button
        icon={<CloseOutlined />}
        onClick={() => setActiveSection('generale')}
        className="close-map-button"
      >
        Fermer la Carte
      </Button>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={currentPos}
        onClick={() => setActiveMarker(null)}
        zoom={14}
        options={mapOptions}
      >
        <MarkerClusterer options={options}>
          {(clusterer) => (
            <>
              {points.map(({ id, type, position, info }) => (
                <Marker
                  key={id}
                  icon={
                    type === OwnershipType.Villa
                      ? './images/villa/villa.png'
                      : type === OwnershipType.Hotel
                      ? './images/hotel/room.png'
                      : type === OwnershipType.Apartment
                      ? './images/appartment/flats.png'
                      : type === OwnershipType.Ground
                      ? './images/ground/grass.png'
                      : ''
                  }
                  position={position}
                  clusterer={clusterer}
                  onClick={() => handleActiveMarker(id)}
                >
                  {activeMarker === id ? (
                    <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                      <div className="info-window">
                        <Title level={5}>{info?.name}</Title>
                        <Text>{info?.city}</Text>
                        <br />
                        <Text>{info?.address}</Text>
                      </div>
                    </InfoWindow>
                  ) : null}
                </Marker>
              ))}
            </>
          )}
        </MarkerClusterer>
      </GoogleMap>
    </div>
  );

  if (loadError) {
    return (
      <CustomPage>
        <div className="error-container">
          <Text type="danger">La carte ne peut pas être chargée pour le moment. Veuillez réessayer plus tard.</Text>
        </div>
      </CustomPage>
    );
  }

  const totalListings = stats.validatedListings + stats.refusedListings + stats.pendingVillas;
  const validatedPercent = totalListings ? (stats.validatedListings / totalListings) * 100 : 0;
  const refusedPercent = totalListings ? (stats.refusedListings / totalListings) * 100 : 0;
  const pendingPercent = totalListings ? (stats.pendingVillas / totalListings) * 100 : 0;

  const renderGenerale = () => (
    <div className="category-card">
      <Title level={3} className="section-title">Vue Générale</Title>
      <div className="stats-grid">
        <div className="stat-item">
          <TeamOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.totalUsers}</div>
            <div className="stat-label">Utilisateurs Total</div>
          </div>
        </div>
        <div className="stat-item">
          <EyeOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.uniqueVisitors}</div>
            <div className="stat-label">Visiteurs Uniques</div>
          </div>
        </div>
        <div className="stat-item">
         <FileDoneOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.totalContrats}</div>
            <div className="stat-label">Total Contrat</div>
          </div>
        </div>
        <div className="stat-item">
          <BarChartOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.totalOwnerships}</div>
            <div className="stat-label">Total Propriétés</div>
          </div>
        </div>
        <div className="stat-item">
          <MessageOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.totalMessages}</div>
            <div className="stat-label">Messages Reçus</div>
          </div>
        </div>
        <div className="stat-item status-charts">
          <div className="stat-content">
            <Title level={5} className="status-title">Répartition des Statuts des Propriétés</Title>
            <div className="progress-container">
              <div className="progress-item">
                <Progress
                  type="circle"
                  percent={validatedPercent}
                  width={60}
                  strokeColor="#2f54eb"
                  format={() => `${validatedPercent.toFixed(1)}%`}
                />
                <div className="stat-label">Acceptées</div>
              </div>
              <div className="progress-item">
                <Progress
                  type="circle"
                  percent={pendingPercent}
                  width={60}
                  strokeColor="#fadb14"
                  format={() => `${pendingPercent.toFixed(1)}%`}
                />
                <div className="stat-label">Attente</div>
              </div>
              <div className="progress-item">
                <Progress
                  type="circle"
                  percent={refusedPercent}
                  width={60}
                  strokeColor="#ff4d4f"
                  format={() => `${refusedPercent.toFixed(1)}%`}
                />
                <div className="stat-label">Rejetées</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUtilisateurs = () => {
    const totalRoles = stats.userRoles.admin + stats.userRoles.agency + stats.userRoles.private + stats.userRoles.user + stats.userRoles.host;
    const adminPercent = totalRoles ? (stats.userRoles.admin / totalRoles) * 100 : 0;
    const agencyPercent = totalRoles ? (stats.userRoles.agency / totalRoles) * 100 : 0;
    const privatePercent = totalRoles ? (stats.userRoles.private / totalRoles) * 100 : 0;
    const userPercent = totalRoles ? (stats.userRoles.user / totalRoles) * 100 : 0;
    const hostPercent = totalRoles ? (stats.userRoles.host / totalRoles) * 100 : 0;

    const conicGradient = totalRoles
      ? `conic-gradient(
          #2f54eb 0% ${adminPercent}%,
          #52c41a ${adminPercent}% ${adminPercent + agencyPercent}%,
          #fadb14 ${adminPercent + agencyPercent}% ${adminPercent + agencyPercent + privatePercent}%,
          #fa8c16 ${adminPercent + agencyPercent + privatePercent}% ${adminPercent + agencyPercent + privatePercent + userPercent}%,
          #722ed1 ${adminPercent + agencyPercent + privatePercent + userPercent}% ${adminPercent + agencyPercent + privatePercent + userPercent + hostPercent}%
        )`
      : 'conic-gradient(#d9d9d9 0% 100%)';

    return (
      <div className="category-card">
        <Title level={3} className="section-title">Utilisateurs</Title>
        <div className="stats-grid">
          <div className="stat-item">
            <TeamOutlined className="stat-icon" />
            <div className="stat-content">
              <div className="stat-value">{stats.activeUsersToday}</div>
              <div className="stat-label">Utilisateurs Actifs Aujourdhui</div>
            </div>
          </div>
          <div className="stat-item">
            <TeamOutlined className="stat-icon" />
            <div className="stat-content">
              <div className="stat-value">{stats.activeUsersWeek}</div>
              <div className="stat-label">Utilisateurs Actifs Cette Semaine</div>
            </div>
          </div>
          <div className="stat-item">
            <TeamOutlined className="stat-icon" />
            <div className="stat-content">
              <div className="stat-value">{stats.activeUsersMonth}</div>
              <div className="stat-label">Utilisateurs Actifs Ce Mois</div>
            </div>
          </div>
          <div className="stat-item">
            <TeamOutlined className="stat-icon" />
            <div className="stat-content">
              <div className="stat-value">{stats.newUsersMonth}</div>
              <div className="stat-label">Nouveaux Utilisateurs Ce Mois</div>
            </div>
          </div>
        </div>
        <div className="list-container">
          <Title level={4} className="list-title">Répartition des Utilisateurs par Rôle</Title>
          <div className="roles-chart-container">
            <div className="roles-progress-container">
              <div className="progress-item">
                <div className="custom-donut" style={{ background: conicGradient }}>
                  <div className="donut-hole">
                    <Text className="donut-total">{totalRoles}</Text>
                    <Text className="donut-label">Users</Text>
                  </div>
                </div>
              </div>
            </div>
            <div className="roles-legend">
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#2f54eb' }}></span>
                <Text>Admins: {stats.userRoles.admin} ({adminPercent.toFixed(1)}%)</Text>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#52c41a' }}></span>
                <Text>Agences: {stats.userRoles.agency} ({agencyPercent.toFixed(1)}%)</Text>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#fadb14' }}></span>
                <Text>Privés: {stats.userRoles.private} ({privatePercent.toFixed(1)}%)</Text>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#fa8c16' }}></span>
                <Text>Utilisateurs: {stats.userRoles.user} ({userPercent.toFixed(1)}%)</Text>
              </div>
              <div className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#722ed1' }}></span>
                <Text>Hôtes: {stats.userRoles.host} ({hostPercent.toFixed(1)}%)</Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAnnonces = () => (
    <div className="category-card">
      <Title level={3} className="section-title">Statistiques Propriétés</Title>
      <div className="stats-grid">
        <div className="stat-item">
          <ShopOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.listingTypes.rent}</div>
            <div className="stat-label">Annonces pour Location</div>
          </div>
        </div>
        <div className="stat-item">
          <DollarOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.listingTypes.sale}</div>
            <div className="stat-label">Annonces pour Vente</div>
          </div>
        </div>
        <div className="stat-item">
          <BarChartOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.offersThisMonth}</div>
            <div className="stat-label">Offres Déposées Ce Mois</div>
          </div>
        </div>
        <div className="stat-item">
          <ClockCircleOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.avgValidationTime} h</div>
            <div className="stat-label">Temps Moyen de Validation</div>
          </div>
        </div>
      </div>
      <div className="list-container">
        <Title level={4} className="list-title">Répartition des Annonces par Statut</Title>
        <div className="list-item">
          <Text>En Attente: <strong>{stats.listingStatuses.pending}</strong></Text>
        </div>
        <div className="list-item">
          <Text>Validées: <strong>{stats.listingStatuses.validated}</strong></Text>
        </div>
        <div className="list-item">
          <Text>Refusées: <strong>{stats.listingStatuses.refused}</strong></Text>
        </div>
      </div>
    </div>
  );

const renderContrats = () => {
  const chartData = stats.contractStats.reduce((acc: any[], stat: ContractStat) => {
    const existing = acc.find(item => item.month === stat.month);
    if (existing) {
      existing[`${stat.type}_${stat.status}`] = stat.count;
    } else {
      acc.push({
        month: stat.month,
        [`${stat.type}_${stat.status}`]: stat.count,
      });
    }
    return acc;
  }, []);

  const barColors: { [key: string]: string } = {
    'rent_granted': '#1890ff', // Bright blue for rent granted
    'rent_pending': '#fadb14', // Yellow for rent pending
    'rent_rejected': '#ff4d4f', // Red for rent rejected
    'sale_granted': '#52c41a', // Green for sale granted
    'sale_pending': '#fa8c16', // Orange for sale pending
    'sale_rejected': '#eb2f96', // Magenta for sale rejected
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{`Mois: ${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.fill }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="category-card">
      <Title level={3} className="section-title">Statistiques des Contrats</Title>
      <div className="stats-grid">
        <div className="stat-item">
          <FileTextOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.contractCount}</div>
            <div className="stat-label">Total des Contrats</div>
          </div>
        </div>
        <div className="stat-item">
          <ShopOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.contractTypes.find(t => t.type === 'rent')?.total || 0}</div>
            <div className="stat-label">Contrats de Location</div>
          </div>
        </div>
        <div className="stat-item">
        <CreditCardOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.contractTypes.find(t => t.type === 'buy')?.total || 0}</div>
            <div className="stat-label">Contrats d Achat</div>
          </div>
        </div>
        <div className="stat-item">
          <CheckCircleOutlined className="stat-icon" />
          <div className="stat-content">
            <div className="stat-value">{stats.contractStatuses.find(s => s.status === 'granted')?.total || 0}</div>
            <div className="stat-label">Contrats Acceptés</div>
          </div>
        </div>
      </div>
      <div className="list-container">
        <Title level={4} className="list-title">Répartition des Contrats par Mois, Type et Statut</Title>
        <div className="histogram-container">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={chartData}
              margin={{ top: 30, right: 40, left: 20, bottom: 20 }}
            >
              <CartesianGrid stroke="#e8ecef" strokeDasharray="5 5" opacity={0.5} />
              <XAxis
                dataKey="month"
                tick={{ fill: '#4b5563', fontSize: 12, fontWeight: 500 }}
                tickLine={false}
                axisLine={{ stroke: '#d1d5db' }}
              />
              <YAxis
                tick={{ fill: '#4b5563', fontSize: 12, fontWeight: 500 }}
                tickLine={false}
                axisLine={{ stroke: '#d1d5db' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{
                  paddingTop: '20px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#4b5563',
                }}
                formatter={(value) => (
                  <span style={{ color: '#4b5563', fontWeight: 500 }}>
                    {value.replace('_', ' - ').charAt(0).toUpperCase() + value.slice(1)}
                  </span>
                )}
              />
              {Object.keys(barColors).map(key => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={`${key.split('_')[0].charAt(0).toUpperCase() + key.split('_')[0].slice(1)} - ${key.split('_')[1].charAt(0).toUpperCase() + key.split('_')[1].slice(1)}`}
                  fill={barColors[key]}
                  stackId="a"
                  radius={[4, 4, 0, 0]}
                  barSize={20}
                  animationDuration={800}
                  animationEasing="ease-in-out"
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

  return (
    <CustomPage>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .home-container {
          background: linear-gradient(135deg, #f0f6ff 0%, #ffffff 100%);
          min-height: calc(100vh - 16vh);
          border-radius: 12px;
          font-family: 'Inter', sans-serif;
        }
        .content-wrapper {
          margin: 0 auto;
          width: 100%;
        }
        .category-card {
          background: #ffffff;
          border-radius: 10px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          padding: 32px;
          margin-bottom: 24px;
          animation: fadeInUp 0.8s ease-out;
          position: relative;
          border: 1px solid #dfe6e9;
        }
        .category-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 6px;
          background: linear-gradient(90deg, #2f54eb, #1a91ff);
          border-radius: 10px 10px 0 0;
        }
        .section-title {
          text-align: left;
          color: #2f54eb;
          margin-bottom: 24px;
          font-weight: 700;
          font-size: 26px;
          position: relative;
          padding-left: 20px;
        }
        .section-title::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 8px;
          height: 28px;
          background: linear-gradient(180deg, #2f54eb, #1a91ff);
          border-radius: 4px;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }
        .stat-item {
          display: flex;
          align-items: center;
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px;
        }
        .status-charts {
          display: flex;
          flex-direction: column;
          align-items: center;
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px;
        }
        .status-title {
          color: #2f54eb;
          margin-bottom: 16px;
          font-weight: 600;
          font-size: 16px;
          text-align: center;
        }
        .progress-container {
          display: flex;
          justify-content: space-around;
          width: 100%;
          gap: 20px;
        }
        .progress-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
        }
        .roles-chart-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 20px;
        }
        .roles-progress-container {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          max-width: 400px;
        }
        .custom-donut {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          position: relative;
          background: conic-gradient(#d9d9d9 0% 100%);
        }
        .donut-hole {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 60px;
          height: 60px;
          background: #ffffff;
          border-radius: 50%;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }
        .donut-total {
          font-size: 18px;
          font-weight: 700;
          color: #2f54eb;
        }
        .donut-label {
          font-size: 10px;
          color: #4b5563;
          text-align: center;
        }
        .roles-legend {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .legend-color {
          width: 16px;
          height: 16px;
          border-radius: 4px;
        }
        .stat-icon {
          font-size: 32px;
          color: #2f54eb;
          margin-right: 12px;
        }
        .stat-content {
          flex: 1;
          text-align: center;
        }
        .stat-value {
          font-size: 22px;
          font-weight: 700;
          color: #2f54eb;
        }
        .stat-label {
          font-size: 13px;
          color: #4b5563;
          font-weight: 500;
        }
        .list-container {
          margin-top: 20px;
        }
        .list-title {
          color: #2f54eb;
          margin-bottom: 12px;
          font-weight: 600;
          font-size: 18px;
        }
        .list-item {
          padding: 10px;
          border-bottom: 1px solid #f1f5f9;
        }
        .list-item:last-child {
          border-bottom: none;
        }
   .histogram-container {
  margin-top: 24px;
  padding: 24px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease-in-out;
}

.histogram-container:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.custom-tooltip {
  background: #ffffff;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #e8ecef;
  font-family: 'Inter', sans-serif;
}

.tooltip-label {
  font-size: 14px;
  font-weight: 600;
  color: #2f54eb;
  margin-bottom: 8px;
}

.custom-tooltip p {
  margin: 4px 0;
  font-size: 13px;
  font-weight: 500;
}
        .button-group {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 24px;
        }
      .section-button {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #fff !important;
  background: linear-gradient(135deg, #2f54eb 0%, #1a91ff 100%) !important;
  border: none !important;
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}
.section-button:hover,
.section-button:active,
.section-button:focus,
.section-button:focus-visible {
  background: linear-gradient(135deg, #2f54eb 0%, #1a91ff 100%) !important;
  outline: none !important;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3) !important;
}
.section-button:disabled {
  background: #2f54ae !important;
  color: #fff !important;
  cursor: not-allowed;
  box-shadow: none !important;
}
.section-button .anticon {
  margin-right: 6px;
  font-size: 16px;
}
        .close-map-button {
          margin-bottom: 4px;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 600;
          color: #000000;
          background: #ffc107;
          border: none;
          border-radius: 8px;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
        }
        .close-map-button .anticon {
          margin-right: 6px;
          font-size: 16px;
        }
        .map-container {
          position: relative;
          width: 100%;
          max-width: 1440px;
          margin: 0 auto;
        }
        .info-window {
          padding: 14px;
          max-width: 260px;
          font-family: 'Inter', sans-serif;
          background: #ffffff;
          border-radius: 10px;
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }
        .info-window h5 {
          margin-bottom: 8px;
          color: #2f54eb;
          font-size: 16px;
        }
        .info-window p {
          margin: 0;
          font-size: 13px;
          color: #4b5563;
        }
        .error-container {
          text-align: center;
          padding: 40px;
          background: #ffffff;
          border-radius: 10px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 700px;
          margin: 40px auto;
        }
        @media (min-width: 1440px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }
          .progress-container {
            flex-direction: row;
            justify-content: space-around;
          }
          .roles-chart-container {
            flex-direction: row;
          }
        }
        @media (max-width: 1024px) {
          .stats-grid { grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
          .section-title { font-size: 24px; }
          .progress-container { flex-direction: row; gap: 15px; }
          .roles-progress-container { max-width: 300px; }
        }
        @media (max-width: 768px) {
          .stats-grid { grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); }
          .category-card { padding: 24px; }
          .stat-value { font-size: 20px; }
          .stat-label { font-size: 12px; }
          .stat-icon { font-size: 28px; }
          .section-title { font-size: 22px; }
          .progress-container { flex-direction: column; align-items: center; gap: 20px; }
          .progress-item { width: 100%; text-align: center; }
          .roles-chart-container { flex-direction: column; align-items: center; }
          .roles-progress-container { max-width: 200px; }
          .custom-donut { width: 100px; height: 100px; }
          .donut-hole { width: 50px; height: 50px; }
          .donut-total { font-size: 16px; }
          .donut-label { font-size: 10px; }
          .roles-legend { align-items: center; }
        }
        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: 1fr; }
          .category-card { padding: 20px; }
          .stat-value { font-size: 18px; }
          .stat-label { font-size: 11px; }
          .stat-icon { font-size: 24px; }
          .section-button, .close-map-button { padding: 8px 12px; font-size: 13px; }
          .section-title { font-size: 20px; }
          .progress-container { flex-direction: column; gap: 15px; }
          .roles-progress-container { max-width: 150px; }
          .custom-donut { width: 80px; height: 80px; }
          .donut-hole { width: 40px; height: 40px; }
          .donut-total { font-size: 14px; }
          .donut-label { font-size: 9px; }
          .roles-legend { align-items: center; }
        }
      `}</style>
      <div className="home-container">
        <div className="content-wrapper">
          <Title level={2} style={{ textAlign: 'left', color: '#2f54eb', fontWeight: 700 }}>
            Tableau de Bord Admin
          </Title>
          {activeSection === 'generale' && renderGenerale()}
          {activeSection === 'utilisateurs' && renderUtilisateurs()}
          {activeSection === 'annonces' && renderAnnonces()}
          {activeSection === 'contrats' && renderContrats()}
          {activeSection === 'map' && isLoaded ? renderMap() : activeSection === 'map' && (
            <div className="error-container">
              <Text>Chargement en cours...</Text>
            </div>
          )}
          <div className="button-group">
            <Button
              icon={<HomeOutlined />}
              onClick={() => setActiveSection('generale')}
              className="section-button"
              disabled={activeSection === 'generale'}
            >
              Vue Générale
            </Button>
            <Button
              icon={<UserOutlined />}
              onClick={() => setActiveSection('utilisateurs')}
              className="section-button"
              disabled={activeSection === 'utilisateurs'}
            >
              Utilisateurs
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => setActiveSection('annonces')}
              className="section-button"
              disabled={activeSection === 'annonces'}
            >
              Statistiques sur Propriété
            </Button>
            <Button
              icon={<SearchOutlined />}
              onClick={() => setActiveSection('contrats')}
              className="section-button"
              disabled={activeSection === 'contrats'}
            >
              Statistiques des Contrats
            </Button>
            <Button
              icon={<EnvironmentOutlined />}
              onClick={() => setActiveSection('map')}
              className="section-button"
              disabled={activeSection === 'map'}
            >
              Afficher la Carte
            </Button>
          </div>
        </div>
      </div>
    </CustomPage>
  );
};

export default Home;